import styled from '@emotion/styled'
import {autocompleteClasses} from '@mui/material/Autocomplete'
import InputBase from '@mui/material/InputBase'
import {Button, Typography, TextField, Chip} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import Box from '@mui/material/Box'
import {InputTitleNormal} from '../TitleStyle'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'

const DropDownButton = styled('div')(() => ({
  padding: 0,
  marginLeft: '8px',
  marginTop: '8px',
  marginBottom: '8px'
}))

const InputAdornmentIcon = styled(InputAdornment)(() => ({
  position: 'absolute'
}))

const SimpleInput = styled(InputBase)(({theme}) => ({
  width: '394px',
  margin: `${'16px'} ${'32px'} ${'0px'} ${'16px'}`,
  height: '32px',
  input: {
    position: 'relative',
    border: `1px solid ${theme.palette.grey[200]}`,
    overflow: 'hidden',
    padding: `${'8px'} ${'40px'}`,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:focus': {border: `1px solid ${theme.palette.hpPrimary}`}
  },
  '&.Mui-error input': {
    border: '1px solid',
    borderColor: theme.palette.hpAccentRed
  }
}))
export const InputError = styled(InputTitleNormal)(({theme}) => ({
  color: theme.palette.hpAccentRed,
  fontSize: theme.typography.subtitle1.fontSize,
  width: '320px',
  margin: '18px'
}))

const SelectAllLabel = styled(Typography)(({theme}) => ({
  color: theme.palette.hpBase,
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.subtitle1.fontSize,
  fontFamily: theme.typography.fontFamily,
  display: 'inline-block'
}))

const ChipTitleHead = styled(Typography)(({theme, isfirst}) => ({
  color: theme.palette.hpBase,
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.subtitle1.fontSize,
  fontFamily: theme.typography.fontFamily,
  marginTop: isfirst === 0 ? '12px' : '28px'
}))

const TitleHead = styled(Typography)(({theme}) => ({
  color: theme.palette.hpAccentBlack,
  paddingLeft: '16px',
  fontWeight: theme.typography.fontWeightSemibold,
  fontSize: theme.typography.subtitle1.fontSize,
  fontFamily: theme.typography.fontFamily
}))

const SdohBox = styled(Box)(({theme}) => ({
  backgroundColor: theme.palette.hpWhite,
  fontSize: theme.typography.subtitle1.fontSize,
  fontFamily: theme.typography.fontFamily,
  width: '430px',
  height: '511px',
  marginLeft: '8px',
  border: '1px solid',
  borderColor: theme.palette.grey[200],
  borderRadius: '4px',
  paddingBottom: '8px'
}))

const SdohDropDown = styled(Box)(() => ({
  overflowY: 'auto',
  width: '430px',
  height: '430px',
  maxHeight: '432px',
  marginTop: '20px',
  marginBottom: '16px'
}))

// TextField-based dropdown (label on border) - now the default style
const StyledTextField = styled(TextField, {shouldForwardProp: (prop) => prop !== 'hasError'})(({theme, hasError}) => ({
  width: '300px',
  '& .MuiOutlinedInput-root': {
    height: '40px',
    backgroundColor: theme.palette.hpWhite,
    fontSize: theme.typography.subtitle1.fontSize,
    fontFamily: theme.typography.fontFamily,
    '& fieldset': {
      borderColor: hasError ? theme.palette.error.main : theme.palette.hpPrimary,
      borderWidth: '2px'
    },
    '&:hover fieldset': {
      borderColor: hasError ? theme.palette.error.main : theme.palette.hpPrimary,
      borderWidth: '2px'
    },
    '&.Mui-focused fieldset': {
      borderColor: hasError ? theme.palette.error.main : theme.palette.hpPrimary,
      borderWidth: '2px'
    }
  },
  '& .MuiInputLabel-root': {
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.hpPrimary,
    '&.Mui-focused': {
      color: hasError ? theme.palette.error.main : theme.palette.hpPrimary
    }
  },
  '& .MuiOutlinedInput-input': {
    padding: '8px 12px',
    cursor: 'pointer',
    caretColor: 'transparent'
  },
  '& .MuiInputBase-root': {
    cursor: 'pointer'
  }
}))

const StyledAutocompletePopperWide = styled('div')(({theme}) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    margin: 0,
    color: 'inherit',
    fontSize: theme.typography.subtitle1.fontSize,
    width: '298px',
    fontFamily: theme.typography.fontFamily
  },
  [`& .${autocompleteClasses.listbox}`]: {
    paddingBottom: '8px'
  },
  [`& .${autocompleteClasses.option}`]: {
    backgroundColor: theme.palette.hpWhite
  }
}))

const StyledPopperWide = styled('div')(({theme}) => ({
  backgroundColor: theme.palette.hpWhite,
  width: '300px',
  fontSize: theme.typography.subtitle1.fontSize,
  marginLeft: '0px',
  marginTop: '4px',
  border: '1px solid',
  borderColor: theme.palette.grey[200],
  borderRadius: '4px',
  position: 'absolute',
  zIndex: 10,
  transform: 'translateY(0px)',
  maxHeight: '400px',
  overflowY: 'auto',
  overflowX: 'auto'
}))

const StyledInput = styled(InputBase)(() => ({
  paddingLeft: '16px'
}))

const StyledSearchIcon = styled(SearchIcon)(({theme}) => ({
  paddingLeft: '8px',
  color: theme.palette.hpBase
}))

const StyledBox = styled(Box, {shouldForwardProp: (prop) => prop !== 'isFocused'})(({theme, isFocused}) => ({
  display: 'flex',
  flexDirection: 'row',
  margin: '10px',
  border: '1px solid',
  borderColor: isFocused ? theme.palette.hpPrimary : theme.palette.grey[200],
  borderRadius: '4px',
  alignItems: 'center'
}))

const ErrorMessage = styled(InputTitleNormal)(({theme}) => ({
  color: theme.palette.hpAccentRed,
  fontSize: '12px',
  width: '320px'
}))

const StyledSpan = styled('span')(({theme}) => ({
  color: theme.palette.hpBase
}))

const StyledList = styled('li', {shouldForwardProp: (prop) => prop !== 'isDisabled'})(({isDisabled}) => ({
  pointerEvents: isDisabled ? 'none' : 'auto',
  opacity: isDisabled ? 0.5 : 1
}))
const StyledButton = styled(Button)(() => ({
  marginLeft: '10px',
  fontStyle: 'italic'
}))

const SelectedItemsContainer = styled(Box)(({theme}) => ({
  backgroundColor: theme.palette.grey[200],
  padding: theme.spacing(2),
  borderRadius: '4px',
  minHeight: '60px',
  '& .MuiChip-root': {
    backgroundColor: theme.palette.hpWhite,
    color: theme.palette.hpAccentBlack
  }
}))

const SelectedItemsHeader = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '12px'
}))

const StyledChipIconButton = styled(IconButton)(() => ({
  width: '2px',
  height: '2px'
}))

const StyledGridItemRelative = styled(Box)(() => ({
  position: 'relative'
}))

const StyledDropdownContainer = styled(Box)(({theme}) => ({
  width: '300px',
  fontSize: theme.typography.subtitle1.fontSize
}))

const StyledChipsContainer = styled(Box)(() => ({
  marginLeft: '0px'
}))

const StyledChip = styled(Chip)(() => ({
  margin: '5px 5px'
}))

export {
  StyledButton,
  StyledTextField,
  StyledAutocompletePopperWide,
  StyledPopperWide,
  StyledInput,
  StyledSearchIcon,
  StyledBox,
  ErrorMessage,
  SimpleInput,
  SdohDropDown,
  SdohBox,
  TitleHead,
  SelectAllLabel,
  ChipTitleHead,
  InputAdornmentIcon,
  DropDownButton,
  StyledSpan,
  StyledList,
  SelectedItemsContainer,
  SelectedItemsHeader,
  StyledChipIconButton,
  StyledGridItemRelative,
  StyledDropdownContainer,
  StyledChipsContainer,
  StyledChip
}
